import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface SettingItem {
  key: string;
  value: string;
  description: string | null;
}

const LABELS: Record<string, string> = {
  free_photo_limit: '免费相册照片数',
  free_video_limit: '免费视频数',
  max_photo_size_mb: '单张照片上限（MB）',
  max_video_size_mb: '单个视频上限（MB）',
};

const HINTS: Record<string, string> = {
  free_photo_limit: '建议 5-10 张。太低会让用户觉得「不花钱没法好好悼念」，转化率反而下降',
  free_video_limit: '建议 1 段。视频存储成本高，先给尝鲜额度',
  max_photo_size_mb: '手机拍摄的照片通常 3-8MB，10MB 足够',
  max_video_size_mb: '视频体积大，50MB 约可存 1-2 分钟片段',
};

export default function AdminSettings() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ items: SettingItem[] }>('/admin/settings').then((res) => {
      if (res.ok && res.data) {
        const map: Record<string, string> = {};
        for (const item of res.data.items) map[item.key] = item.value;
        setValues(map);
      }
      setLoading(false);
    });
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    const res = await api('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify({ values }),
    });

    setSaving(false);
    if (res.ok) {
      setNotice('已保存，额度立即生效，无需重新部署');
    } else {
      setError(res.error?.message ?? '保存失败');
    }
  }

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <form onSubmit={save} className="max-w-xl">
      <div className="mb-5 rounded-xl border border-candle-400/40 bg-candle-300/10 p-4 text-xs leading-relaxed text-ink-700">
        这里调整的是<strong>免费额度</strong>。修改后全站立即生效，已上传的文件不受影响。
        <br />
        付费扩容能力在「道具管理」里配置（分类：扩容）。
      </div>

      <div className="space-y-5">
        {Object.keys(LABELS).map((key) => (
          <div key={key}>
            <label className="mb-1.5 block text-sm text-ink-800">{LABELS[key]}</label>
            <input
              type="number"
              min={0}
              max={99999}
              value={values[key] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              className="h-11 w-40 rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
            {HINTS[key] && (
              <p className="mt-1.5 text-[11px] leading-relaxed text-ink-500">{HINTS[key]}</p>
            )}
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
      )}
      {notice && (
        <p className="mt-5 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {notice}
        </p>
      )}

      <button
        type="submit"
        disabled={saving}
        className="mt-6 h-11 rounded-xl bg-ink-900 px-8 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
      >
        {saving ? '保存中…' : '保存设置'}
      </button>
    </form>
  );
}
